import { marked } from 'marked';
import React, {
    ChangeEvent,
    ClipboardEvent,
    DragEvent,
    FC,
    KeyboardEvent,
    PointerEvent as ReactPointerEvent,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { ChatBubble, ChatContainer, ChatInput, ChatInputArea, ChatSendButton } from 'src/components/shared/aiAssistant/chatPrimitives';
import Stack from 'src/components/widgets/stack/stack';
import { useSideImagesNoCreditsDialog } from 'src/components/pages/productConfiguration/dialogs/sideImagesNoCreditsDialog';
import { BasicButton, GhostButton } from 'src/components/widgets/buttons';
import { Icon } from 'src/components/widgets/icon';
import AttachFileIcon from 'src/shared/material_icons/editor/attach_file.svg';
import RestartIcon from 'src/shared/material_icons/device/restart_alt.svg';
import { T, uploadFile } from 'src/shared/helpers';
import styled from 'styled-components';
import { useGenerationPreflight } from 'src/components/pages/aiStudio/hooks/useGenerationPreflight';
import { AgentGestureCard } from './AgentGestureCard';
import { AgentPricingUpgradeCard } from './AgentPricingUpgradeCard';
import { AgentTaskPreview, hasUsefulAgentPreview } from './AgentTaskPreview';
import { AgentDraft, AgentGeneratedImage, AgentGesture, LocalAgentAttachment, AgentAnswerRecap, AgentQuestionnaire } from './types';
import { AgentQuestionWizard } from './AgentQuestionWizard';
import { followsWidgetAnswers, hasQuestionnaireChoices } from './agentInteractionState';
import { AgentActivityIndicator, AgentAnswerRecapCard, AgentResultRecapCard } from './AgentCommunicationCards';
import { messageForOutcome, useAgentSession } from './useAgentSession';

const MAX_ATTACHMENT_BYTES = 12 * 1024 * 1024;
const DEFAULT_SPLIT_RATIO = 0.45;
const MIN_SPLIT_RATIO = 0.3;
const MAX_SPLIT_RATIO = 0.7;

interface UploadingAttachment extends LocalAgentAttachment {
    status: 'uploading' | 'ready' | 'failed';
    error?: string;
}

const Root = styled.div<{ $splitRatio: number; $hasPreview: boolean }>`
    display: grid;
    grid-template-rows: auto minmax(0, 1fr);
    flex: 1;
    min-width: 0;
    min-height: 0;
    background: white;

    @container (min-width: 900px) {
        grid-template-rows: minmax(0, 1fr);
        grid-template-columns: ${(props) => (props.$hasPreview ? `minmax(0, ${props.$splitRatio}fr) 18px minmax(0, ${1 - props.$splitRatio}fr)` : 'minmax(0, 1fr)')};
    }
`;

const PanelTabs = styled.div`
    display: flex;
    gap: 4px;
    padding: 8px 12px;
    border-bottom: 1px solid #e5e7eb;
    background: white;

    @container (min-width: 900px) {
        display: none;
    }
`;

const TabButton = styled.button<{ $active: boolean }>`
    flex: 1;
    padding: 8px 12px;
    border: 0;
    border-radius: 7px;
    background: ${(props) => (props.$active ? '#e0f7fa' : 'transparent')};
    color: ${(props) => (props.$active ? '#007c91' : '#607d8b')};
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
`;

const ConversationPanel = styled.section<{ $visible: boolean }>`
    display: ${(props) => (props.$visible ? 'flex' : 'none')};
    min-width: 0;
    min-height: 0;
    flex-direction: column;
    padding: 16px;
    background: white;

    @container (min-width: 900px) {
        display: flex;
    }
`;

const PreviewPanel = styled.section<{ $visible: boolean }>`
    display: ${(props) => (props.$visible ? 'flex' : 'none')};
    min-width: 0;
    min-height: 0;

    @container (min-width: 900px) {
        display: flex;
    }
`;

const SplitHandle = styled.button<{ $dragging: boolean }>`
    display: none;
    position: relative;
    width: 18px;
    height: 100%;
    margin: 0;
    padding: 0;
    border: 0;
    background: #f1f4f5;
    box-shadow:
        inset 1px 0 #e5e7eb,
        inset -1px 0 #e5e7eb;
    color: #b0bec5;
    cursor: col-resize;
    touch-action: none;
    user-select: none;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        left: 8px;
        width: 1px;
        background: #e5e7eb;
        transition: background 0.15s ease;
    }

    &::after {
        content: '⋮';
        position: absolute;
        top: 50%;
        left: 50%;
        display: flex;
        width: 16px;
        height: 38px;
        align-items: center;
        justify-content: center;
        border: 1px solid #cfd8dc;
        border-radius: 999px;
        background: white;
        color: currentColor;
        font-size: 18px;
        line-height: 1;
        transform: translate(-50%, -50%);
        transition:
            border-color 0.15s ease,
            color 0.15s ease,
            box-shadow 0.15s ease;
    }

    @container (min-width: 900px) {
        display: block;
    }

    &:hover,
    &:focus-visible {
        color: #007c91;
        outline: none;

        &::before {
            background: #80deea;
        }

        &::after {
            border-color: #80deea;
            box-shadow: 0 2px 8px rgba(0, 124, 145, 0.18);
        }
    }

    ${(props) =>
        props.$dragging &&
        `
            color: #007c91;

            &::before {
                background: #80deea;
            }

            &::after {
                border-color: #80deea;
                box-shadow: 0 2px 8px rgba(0, 124, 145, 0.18);
            }
        `}
`;

const ConversationTools = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
`;

const ConversationState = styled.span`
    color: #78909c;
    font-size: 11px;
`;

const Transcript = styled(ChatContainer)`
    gap: 14px;
    padding: 4px 4px 16px;
`;

const Welcome = styled.div`
    display: flex;
    flex: 1;
    min-height: 240px;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 12px;
    padding: 32px 18px;
    text-align: center;

    h3 {
        margin: 0;
        color: #263238;
        font-size: 18px;
    }

    p {
        max-width: 420px;
        margin: 0;
        color: #607d8b;
        font-size: 13px;
        line-height: 1.5;
    }
`;

const Suggestions = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px;
    margin-top: 4px;
`;

const MessageGroup = styled.div<{ $merchant?: boolean }>`
    display: flex;
    align-items: ${(props) => (props.$merchant ? 'flex-end' : 'flex-start')};
    flex-direction: column;
    gap: 6px;
`;

const AgentBubble = styled(ChatBubble)`
    max-width: min(92%, 720px);
    white-space: normal;
`;

const AgentMarkdown = styled.div`
    overflow-wrap: anywhere;

    p {
        margin: 0 0 8px;
    }

    p:last-child {
        margin-bottom: 0;
    }

    ul,
    ol {
        margin: 0 0 8px;
        padding-left: 20px;
    }

    li + li {
        margin-top: 4px;
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        margin: 12px 0 6px;
        color: #263238;
        line-height: 1.3;
    }

    h1 {
        font-size: 18px;
    }

    h2 {
        font-size: 16px;
    }

    h3,
    h4,
    h5,
    h6 {
        font-size: 14px;
    }

    blockquote {
        margin: 8px 0;
        padding-left: 10px;
        border-left: 3px solid #cfd8dc;
        color: #607d8b;
    }

    code {
        padding: 1px 4px;
        border-radius: 4px;
        background: #eceff1;
        font-family: Consolas, Monaco, monospace;
        font-size: 0.92em;
    }

    pre {
        overflow-x: auto;
        margin: 8px 0;
        padding: 10px;
        border-radius: 7px;
        background: #eceff1;

        code {
            padding: 0;
            background: transparent;
        }
    }

    a {
        color: #7b1fa2;
        text-decoration: underline;
        overflow-wrap: anywhere;
    }
`;

const MerchantBubble = styled(ChatBubble)`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const MessageText = styled.div`
    white-space: pre-wrap;
`;

const SentImages = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;

    img {
        width: 64px;
        height: 64px;
        border-radius: 7px;
        object-fit: cover;
    }
`;

const Options = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 7px;
    margin-top: 10px;
`;

const OptionButton = styled(BasicButton)`
    max-width: 100%;
    min-width: 0;
    height: auto;
    min-height: 24px;
    justify-content: flex-start;
    padding: 5px 9px;
    text-align: left;
    white-space: normal;
    overflow-wrap: anywhere;
`;

const Notice = styled.div`
    padding: 10px 12px;
    border: 1px solid #ffe0b2;
    border-radius: 9px;
    background: #fff8e1;
    color: #8d6e63;
    font-size: 12px;
`;

const ErrorNotice = styled(Notice)`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: 8px 0;
    border-color: #ffcdd2;
    background: #ffebee;
    color: #b71c1c;
`;

const ApprovalCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 12px;
    padding: 12px;
    border: 1px solid #ffcc80;
    border-radius: 10px;
    background: #fff8e1;

    strong {
        color: #e65100;
        font-size: 12px;
    }

    p {
        margin: 0;
        color: #5d4037;
        font-size: 12px;
    }
`;

const GeneratedImages = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 8px;
    margin-top: 12px;
`;

const GeneratedImageCard = styled.div`
    overflow: hidden;
    border: 1px solid #dfe5e8;
    border-radius: 10px;
    background: #fafbfc;

    > a,
    > div:first-child {
        display: block;
        aspect-ratio: 1;
        background: #eceff1;
    }

    img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: contain;
    }
`;

const GeneratedImageInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 8px;

    strong {
        color: #263238;
        font-size: 11px;
    }

    span {
        color: #78909c;
        font-size: 10px;
        line-height: 1.35;
    }
`;

const NoCreditsCard = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-top: 10px;
    padding: 10px 12px;
    border: 1px solid #d1c4e9;
    border-radius: 9px;
    background: #f3e5f5;
    color: #4a148c;
    font-size: 11px;

    span {
        line-height: 1.4;
    }
`;

const Composer = styled.div`
    flex-shrink: 0;
`;

const ComposerDropZone = styled.div<{ $dragging: boolean }>`
    position: relative;
    border-radius: 9px;
    transition:
        background-color 0.15s ease,
        box-shadow 0.15s ease;

    ${(props) =>
        props.$dragging &&
        `
            background: #e0f7fa;
            box-shadow: 0 0 0 2px #80deea;
        `}
`;

const ComposerDropHint = styled.div`
    position: absolute;
    z-index: 1;
    top: 4px;
    left: 50%;
    padding: 3px 9px;
    border-radius: 999px;
    background: #007c91;
    color: white;
    font-size: 11px;
    font-weight: 600;
    pointer-events: none;
    transform: translateX(-50%);
`;

const Attachments = styled.div`
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding: 8px 0;
`;

const Attachment = styled.div`
    position: relative;
    display: grid;
    width: 150px;
    min-width: 150px;
    grid-template-columns: 42px 1fr;
    gap: 7px;
    padding: 6px;
    border: 1px solid #dfe5e8;
    border-radius: 9px;
    background: #fafbfc;

    img {
        width: 42px;
        height: 42px;
        border-radius: 6px;
        object-fit: cover;
    }
`;

const AttachmentInfo = styled.div`
    min-width: 0;

    > span {
        display: block;
        overflow: hidden;
        color: #455a64;
        font-size: 10px;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    > small {
        color: #78909c;
        font-size: 9px;
    }
`;

const AttachmentSelect = styled.select`
    width: 100%;
    margin-top: 4px;
    border: 1px solid #cfd8dc;
    border-radius: 4px;
    background: white;
    font-size: 9px;
`;

const RemoveAttachment = styled.button`
    position: absolute;
    right: 2px;
    top: 2px;
    width: 18px;
    height: 18px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: rgba(38, 50, 56, 0.76);
    color: white;
    cursor: pointer;
    font-size: 12px;
`;

const HiddenFileInput = styled.input`
    display: none;
`;

const AttachButton = styled(BasicButton)`
    flex-shrink: 0;
`;

const findPendingProposal = (draft: AgentDraft) => {
    const suggestions = draft.document?._meta?.suggestions ?? [];
    for (const variant of draft.document?.product?.variants ?? []) {
        for (const side of variant.sides ?? []) {
            if (!side.entityReferenceKey || !side.image?.url || (side.areas?.length ?? 0) > 0) continue;
            if (suggestions.some((suggestion) => suggestion.subject === side.entityReferenceKey && suggestion.value?.widthPx && suggestion.value.heightPx))
                return side;
        }
    }
    return null;
};

const turnText = (message: string) => {
    const html = marked(message.replace(/\\n/g, '\n'), { breaks: true, gfm: true }) as string;

    return <AgentMarkdown dangerouslySetInnerHTML={{ __html: html }} />;
};

const uploadId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const isImageFile = (file: File | null): file is File => Boolean(file?.type.startsWith('image/'));

const getImageFilesFromDataTransfer = (dataTransfer: DataTransfer | null) => {
    if (!dataTransfer) return [];

    const itemFiles = Array.from(dataTransfer.items ?? [])
        .filter((item) => item.kind === 'file')
        .map((item) => item.getAsFile())
        .filter(isImageFile);

    if (itemFiles.length > 0) return itemFiles;
    return Array.from(dataTransfer.files ?? []).filter(isImageFile);
};

const containsFiles = (dataTransfer: DataTransfer) =>
    Array.from(dataTransfer.types ?? []).includes('Files') ||
    dataTransfer.files.length > 0 ||
    Array.from(dataTransfer.items ?? []).some((item) => item.kind === 'file');

export interface AgentAssistantProps {
    onExternalChanges?: () => void;
    /** Starts a new, empty conversation with this message. Restored conversations are never changed. */
    initialPrompt?: string;
}

export const AgentAssistant: FC<AgentAssistantProps> = ({ onExternalChanges, initialPrompt }) => {
    const {
        conversationId,
        currentRequest,
        isRefreshingResult,
        sendAnswers,
        entries,
        draft,
        isBusy,
        isDraftBusy,
        isDraftRefreshRequired,
        refreshPreview,
        isRestoring,
        error,
        canRetry,
        hasPendingRequest,
        send,
        retry,
        upsertPrintArea,
        replaceSideImage,
        deletePrintArea,
        restorePrintArea,
        setSideScale,
        reset
    } = useAgentSession(onExternalChanges);
    const { showSideImagesNoCreditsDialog } = useSideImagesNoCreditsDialog();
    const { runWithPreflight, isAIStudioAddonActive, isActivatingAddonInProgress } = useGenerationPreflight();
    const [activePanel, setActivePanel] = useState<'conversation' | 'preview'>('conversation');
    const [answerCorrection, setAnswerCorrection] = useState<{ questionnaire: AgentQuestionnaire; recap: AgentAnswerRecap; afterTurnId: number } | null>(null);
    const hasPreview = hasUsefulAgentPreview(draft);
    useEffect(() => {
        if (!hasPreview) setActivePanel('conversation');
    }, [hasPreview]);
    const [message, setMessage] = useState('');
    const [attachments, setAttachments] = useState<UploadingAttachment[]>([]);
    const [composerError, setComposerError] = useState<string | null>(null);
    const [splitRatio, setSplitRatio] = useState(DEFAULT_SPLIT_RATIO);
    const [isSplitDragging, setIsSplitDragging] = useState(false);
    const [isDraggingAttachments, setIsDraggingAttachments] = useState(false);
    const [isRetryPreflightInProgress, setIsRetryPreflightInProgress] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const transcriptRef = useRef<HTMLDivElement>(null);
    const splitRootRef = useRef<HTMLDivElement>(null);
    const attachmentDragDepthRef = useRef(0);
    const sentInitialPromptRef = useRef<string | null>(null);
    const normalizedInitialPrompt = initialPrompt?.trim() ?? '';

    const variants = draft.document?.product?.variants ?? [];
    const variantNames = [...new Set(variants.map((variant) => variant.name).filter((name): name is string => Boolean(name)))];
    const sideNames = [
        ...new Set(
            variants
                .flatMap((variant) => variant.sides ?? [])
                .map((side) => side.name)
                .filter((name): name is string => Boolean(name))
        )
    ];
    const latestAgentEntry = [...entries].reverse().find((entry) => entry.role === 'agent');
    const lastAgentTurn = latestAgentEntry?.role === 'agent' ? latestAgentEntry.turn : null;
    const hasLaterSubmission = latestAgentEntry
        ? entries.slice(entries.findIndex((entry) => entry.id === latestAgentEntry.id) + 1).some((entry) => entry.role === 'merchant' && entry.requestId)
        : false;
    const activeQuestionnaire = !hasLaterSubmission && hasQuestionnaireChoices(lastAgentTurn?.questionnaire) ? lastAgentTurn?.questionnaire : null;
    const correctionWasSent =
        answerCorrection &&
        entries.some(
            (entry) =>
                entry.role === 'merchant' &&
                entry.answers?.turnId === answerCorrection.questionnaire.turnId &&
                entry.answers?.correctionAfterTurnId === answerCorrection.afterTurnId
        );
    const wizardQuestionnaire = !correctionWasSent && answerCorrection ? answerCorrection.questionnaire : activeQuestionnaire;
    useEffect(() => {
        if (!conversationId || isRestoring) return;
        const prefix = `zakeke.agent.questions.${conversationId}.`;
        const activeKey = wizardQuestionnaire ? `${prefix}${wizardQuestionnaire.turnId}.${answerCorrection?.afterTurnId ?? 'new'}` : null;
        try {
            Object.keys(window.sessionStorage).forEach((key) => {
                if (key.startsWith(prefix) && key !== activeKey) window.sessionStorage.removeItem(key);
            });
        } catch {
            // Draft storage is optional.
        }
    }, [conversationId, isRestoring, wizardQuestionnaire?.turnId, answerCorrection?.afterTurnId]);
    const pendingProposal = useMemo(() => findPendingProposal(draft), [draft]);
    const isInteractionBusy = isBusy || hasPendingRequest || isDraftBusy || isActivatingAddonInProgress || isRetryPreflightInProgress;

    const retryFailedImages = (images: AgentGeneratedImage[]) => {
        if (images.length === 0 || isInteractionBusy || isActivatingAddonInProgress || isRetryPreflightInProgress) return;

        setIsRetryPreflightInProgress(true);

        const retryMessage = T._('Retry the image generation using the same details.', 'Admin');

        void runWithPreflight(
            async () => {
                await send(retryMessage);
            },
            {
                // The first pack is a one-time bonus and must never opt the seller into paid auto-refills.
                autoRefill: false,
                onNoCredits: showSideImagesNoCreditsDialog
            }
        ).finally(() => setIsRetryPreflightInProgress(false));
    };

    const updateSplitRatio = (nextRatio: number) => {
        setSplitRatio(Math.min(Math.max(nextRatio, MIN_SPLIT_RATIO), MAX_SPLIT_RATIO));
    };

    const handleSplitPointerDown = (event: ReactPointerEvent<HTMLButtonElement>) => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        setIsSplitDragging(true);
        document.body.style.userSelect = 'none';
    };

    const handleSplitPointerMove = (event: ReactPointerEvent<HTMLButtonElement>) => {
        if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
        const rootBounds = splitRootRef.current?.getBoundingClientRect();
        if (!rootBounds || rootBounds.width <= 0) return;
        updateSplitRatio((event.clientX - rootBounds.left) / rootBounds.width);
    };

    const handleSplitPointerEnd = (event: ReactPointerEvent<HTMLButtonElement>) => {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
        setIsSplitDragging(false);
        document.body.style.userSelect = '';
    };

    const handleSplitKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
        const step = event.shiftKey ? 0.1 : 0.05;
        if (event.key === 'Home') {
            event.preventDefault();
            updateSplitRatio(MIN_SPLIT_RATIO);
        } else if (event.key === 'End') {
            event.preventDefault();
            updateSplitRatio(MAX_SPLIT_RATIO);
        } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            updateSplitRatio(splitRatio - step);
        } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            updateSplitRatio(splitRatio + step);
        }
    };

    useEffect(
        () => () => {
            document.body.style.userSelect = '';
        },
        []
    );

    useEffect(() => {
        transcriptRef.current?.scrollTo({ top: transcriptRef.current.scrollHeight, behavior: 'smooth' });
    }, [entries, isBusy, attachments.length]);

    useEffect(() => {
        if (
            !normalizedInitialPrompt ||
            isRestoring ||
            isBusy ||
            conversationId !== null ||
            entries.length > 0 ||
            sentInitialPromptRef.current === normalizedInitialPrompt
        )
            return;

        sentInitialPromptRef.current = normalizedInitialPrompt;
        void send(normalizedInitialPrompt);
    }, [conversationId, entries.length, isBusy, isRestoring, normalizedInitialPrompt, send]);

    const updateAttachment = (id: string, values: Partial<UploadingAttachment>) => {
        setAttachments((current) => current.map((attachment) => (attachment.id === id ? { ...attachment, ...values } : attachment)));
    };

    const uploadAttachmentFiles = async (files: File[]) => {
        if (isInteractionBusy) return;

        const imageFiles = files.filter(isImageFile);
        if (imageFiles.length === 0) return;
        setComposerError(null);

        for (const file of imageFiles) {
            if (file.size > MAX_ATTACHMENT_BYTES) {
                setComposerError(T._('Images must be smaller than 12 MB.', 'Admin'));
                continue;
            }

            const id = uploadId();
            const previewUrl = URL.createObjectURL(file);
            setAttachments((current) => [...current, { id, name: file.name, url: '', previewUrl, status: 'uploading' }]);

            try {
                const url = await uploadFile(file);
                updateAttachment(id, { url, status: 'ready' });
            } catch {
                updateAttachment(id, { status: 'failed', error: T._('Upload failed', 'Admin') });
            }
        }
    };

    const attachFiles = (event: ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files ?? []);
        event.target.value = '';
        void uploadAttachmentFiles(files);
    };

    const handleComposerDragEnter = (event: DragEvent<HTMLDivElement>) => {
        if (isInteractionBusy || !containsFiles(event.dataTransfer)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
        attachmentDragDepthRef.current += 1;
        setIsDraggingAttachments(true);
    };

    const handleComposerDragOver = (event: DragEvent<HTMLDivElement>) => {
        if (isInteractionBusy || !containsFiles(event.dataTransfer)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
        setIsDraggingAttachments(true);
    };

    const handleComposerDragLeave = (event: DragEvent<HTMLDivElement>) => {
        if (!containsFiles(event.dataTransfer)) return;
        event.preventDefault();
        attachmentDragDepthRef.current = Math.max(attachmentDragDepthRef.current - 1, 0);
        if (attachmentDragDepthRef.current === 0) setIsDraggingAttachments(false);
    };

    const handleComposerDrop = (event: DragEvent<HTMLDivElement>) => {
        if (!containsFiles(event.dataTransfer)) return;
        event.preventDefault();
        attachmentDragDepthRef.current = 0;
        setIsDraggingAttachments(false);
        void uploadAttachmentFiles(getImageFilesFromDataTransfer(event.dataTransfer));
    };

    const handleComposerPaste = (event: ClipboardEvent<HTMLDivElement>) => {
        if (isInteractionBusy) return;
        const imageFiles = getImageFilesFromDataTransfer(event.clipboardData);
        if (imageFiles.length === 0) return;
        event.preventDefault();
        void uploadAttachmentFiles(imageFiles);
    };

    const removeAttachment = (id: string) => {
        setAttachments((current) => {
            const attachment = current.find((item) => item.id === id);
            if (attachment) URL.revokeObjectURL(attachment.previewUrl);
            return current.filter((item) => item.id !== id);
        });
    };

    const sendCurrentMessage = () => {
        if (attachments.some((attachment) => attachment.status === 'uploading')) {
            setComposerError(T._('Wait for the images to finish uploading.', 'Admin'));
            return;
        }

        const readyAttachments = attachments.filter((attachment) => attachment.status === 'ready');
        if (!message.trim() && readyAttachments.length === 0) return;
        void send(message, readyAttachments);
        setAnswerCorrection(null);
        attachments.filter((attachment) => attachment.status !== 'ready').forEach((attachment) => URL.revokeObjectURL(attachment.previewUrl));
        setMessage('');
        setAttachments([]);
        setComposerError(null);
    };

    const submitGesture = (gestureMessage: string, gesture?: AgentGesture | null) => {
        void send(gestureMessage, [], gesture ?? null);
    };

    const onComposerKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            sendCurrentMessage();
        }
    };

    const restart = () => {
        attachments.forEach((attachment) => URL.revokeObjectURL(attachment.previewUrl));
        setAttachments([]);
        setMessage('');
        setComposerError(null);
        setActivePanel('conversation');
        setAnswerCorrection(null);
        sentInitialPromptRef.current = null;
        reset();
    };

    return (
        <Root ref={splitRootRef} $splitRatio={splitRatio} $hasPreview={hasPreview}>
            <PanelTabs role='tablist'>
                <TabButton type='button' role='tab' $active={activePanel === 'conversation'} onClick={() => setActivePanel('conversation')}>
                    {T._('Conversation', 'Admin')}
                </TabButton>
                {hasPreview && (
                    <TabButton type='button' role='tab' $active={activePanel === 'preview'} onClick={() => setActivePanel('preview')}>
                        {T._('Preview', 'Admin')}
                    </TabButton>
                )}
            </PanelTabs>

            <ConversationPanel $visible={!hasPreview || activePanel === 'conversation'}>
                <ConversationTools>
                    <ConversationState>
                        {conversationId ? `${T._('Conversation', 'Admin')} #${conversationId}` : T._('New conversation', 'Admin')}
                    </ConversationState>
                    <GhostButton type='button' $size='small' onClick={restart} disabled={isInteractionBusy || isRestoring}>
                        <Icon>
                            <RestartIcon />
                        </Icon>
                        {T._('Start over', 'Admin')}
                    </GhostButton>
                </ConversationTools>

                <Transcript ref={transcriptRef}>
                    {entries.length === 0 && !isRestoring && (
                        <Welcome>
                            <h3>{T._('What would you like to do?', 'Admin')}</h3>
                            <p>{T._('Describe what you need in your own words. The assistant will guide you through the available steps.', 'Admin')}</p>
                            <Suggestions>
                                <BasicButton type='button' $size='small' onClick={() => void send(T._('Show me what you can help me with.', 'Admin'))}>
                                    {T._('Explore capabilities', 'Admin')}
                                </BasicButton>
                                <BasicButton type='button' $size='small' onClick={() => void send(T._('Help me get started with a new task.', 'Admin'))}>
                                    {T._('Start a task', 'Admin')}
                                </BasicButton>
                            </Suggestions>
                        </Welcome>
                    )}

                    {entries.map((entry, entryIndex) => {
                        if (entry.role === 'notice') return <Notice key={entry.id}>{entry.message}</Notice>;
                        if (entry.role === 'merchant') {
                            return (
                                <MessageGroup key={entry.id} $merchant>
                                    <MerchantBubble $role='user'>
                                        {entry.attachments.length > 0 && (
                                            <SentImages>
                                                {entry.attachments.map((attachment) => (
                                                    <img key={attachment.id} src={attachment.previewUrl || attachment.url} alt={attachment.name} />
                                                ))}
                                            </SentImages>
                                        )}
                                        {entry.text && <MessageText>{entry.text}</MessageText>}
                                        {entry.answers && (
                                            <AgentAnswerRecapCard
                                                recap={entry.answers}
                                                disabled={isInteractionBusy || hasLaterSubmission}
                                                onEdit={
                                                    lastAgentTurn?.turnId &&
                                                    entries.some((item) => item.role === 'agent' && item.turn.questionnaire?.turnId === entry.answers!.turnId)
                                                        ? () => {
                                                              const source = entries.find(
                                                                  (item) => item.role === 'agent' && item.turn.questionnaire?.turnId === entry.answers!.turnId
                                                              );
                                                              if (source?.role === 'agent' && source.turn.questionnaire) {
                                                                  setAnswerCorrection({
                                                                      questionnaire: source.turn.questionnaire,
                                                                      recap: entry.answers!,
                                                                      afterTurnId: lastAgentTurn.turnId!
                                                                  });
                                                                  setActivePanel('conversation');
                                                              }
                                                          }
                                                        : undefined
                                                }
                                            />
                                        )}
                                    </MerchantBubble>
                                </MessageGroup>
                            );
                        }

                        const { turn } = entry;
                        const text = turn.question || turn.message;
                        const isLatest = latestAgentEntry?.id === entry.id;
                        const approval =
                            turn.approval ??
                            (draft.document?._meta?.approvalOffer && draft.document._meta.approvalOffer.plannedInTurn === turn.turnNumber
                                ? draft.document._meta.approvalOffer
                                : null);
                        const syntheticGesture: AgentGesture | null =
                            isLatest && !turn.gesture && !turn.questionnaire && pendingProposal?.entityReferenceKey
                                ? { kind: 'area', sideRef: pendingProposal.entityReferenceKey, x1: 0, y1: 0, x2: 0, y2: 0 }
                                : null;

                        return (
                            <MessageGroup key={entry.id}>
                                <AgentBubble $role='assistant'>
                                    {text && turnText(text)}
                                    {turn.resultRecap && followsWidgetAnswers(entries, entryIndex) && (
                                        <AgentResultRecapCard recap={turn.resultRecap} onPreview={hasPreview ? () => setActivePanel('preview') : undefined} />
                                    )}
                                    {turn.generatedImages && turn.generatedImages.length > 0 && (
                                        <GeneratedImages>
                                            {turn.generatedImages.map((image) => (
                                                <GeneratedImageCard key={`${image.taskId}:${image.sideRef}`}>
                                                    {image.url ? (
                                                        <a href={image.url} target='_blank' rel='noreferrer'>
                                                            <img src={image.url} alt={image.prompt || image.sideRef} />
                                                        </a>
                                                    ) : (
                                                        <div />
                                                    )}
                                                    <GeneratedImageInfo>
                                                        <strong>{image.sideRef}</strong>
                                                        <span>
                                                            {image.errorCode === 'addon_not_active'
                                                                ? T._('Activate AI Studio to generate images.', 'Admin')
                                                                : image.errorCode === 'no_credits'
                                                                  ? T._('Not enough AI credits', 'Admin')
                                                                  : image.errorMessage ||
                                                                    (image.state.toLowerCase() === 'completed' ? T._('Generated image', 'Admin') : image.state)}
                                                        </span>
                                                    </GeneratedImageInfo>
                                                </GeneratedImageCard>
                                            ))}
                                        </GeneratedImages>
                                    )}
                                    {turn.generatedImages?.some((image) => image.errorCode === 'no_credits' || image.errorCode === 'addon_not_active') && (
                                        <NoCreditsCard>
                                            <span>
                                                {isAIStudioAddonActive && !turn.generatedImages?.some((image) => image.errorCode === 'addon_not_active')
                                                    ? T._('You need additional AI credits to generate these images.', 'Admin')
                                                    : T._('Activate AI Studio to retry these images.', 'Admin')}
                                            </span>
                                            <BasicButton
                                                type='button'
                                                $size='small'
                                                disabled={isInteractionBusy || isActivatingAddonInProgress || isRetryPreflightInProgress}
                                                onClick={() =>
                                                    isAIStudioAddonActive && !turn.generatedImages?.some((image) => image.errorCode === 'addon_not_active')
                                                        ? showSideImagesNoCreditsDialog()
                                                        : retryFailedImages(
                                                              turn.generatedImages!.filter(
                                                                  (image) => image.errorCode === 'no_credits' || image.errorCode === 'addon_not_active'
                                                              )
                                                          )
                                                }
                                            >
                                                {isActivatingAddonInProgress
                                                    ? T._('Activating…', 'Admin')
                                                    : isRetryPreflightInProgress
                                                      ? T._('Checking…', 'Admin')
                                                      : isAIStudioAddonActive && !turn.generatedImages?.some((image) => image.errorCode === 'addon_not_active')
                                                        ? T._('Purchase credits', 'Admin')
                                                        : T._('Activate and retry', 'Admin')}
                                            </BasicButton>
                                        </NoCreditsCard>
                                    )}
                                    {turn.requiredPlanFeatures?.includes('advanced_pricing') && (
                                        <AgentPricingUpgradeCard
                                            busy={isInteractionBusy}
                                            onResume={() =>
                                                send(T._('Check Advanced Pricing availability and resume the configuration for my review.', 'Admin'))
                                            }
                                        />
                                    )}
                                    {turn.outcomeCode && <Notice>{messageForOutcome(turn.outcomeCode)}</Notice>}
                                    {!turn.questionnaire && !approval && turn.options && turn.options.length > 0 && (
                                        <Options>
                                            {turn.options.map((option) => (
                                                <OptionButton
                                                    key={option}
                                                    type='button'
                                                    $size='small'
                                                    disabled={!isLatest || isInteractionBusy}
                                                    onClick={() => void send(option)}
                                                >
                                                    {option}
                                                </OptionButton>
                                            ))}
                                        </Options>
                                    )}
                                    {approval && (
                                        <ApprovalCard>
                                            <strong>{T._('Approval required', 'Admin')}</strong>
                                            <p>
                                                {approval.description || T._('The assistant has prepared a plan that would update your Zakeke data.', 'Admin')}
                                            </p>
                                            <p>{T._('Review these changes before applying them.', 'Admin')}</p>
                                            <Stack $horizontal $gap='8px'>
                                                <BasicButton
                                                    type='button'
                                                    $size='small'
                                                    disabled={!isLatest || hasLaterSubmission || isInteractionBusy}
                                                    onClick={() => void send(T._('I approve the changes in the proposed plan. Apply that plan.', 'Admin'))}
                                                >
                                                    {T._('Confirm changes', 'Admin')}
                                                </BasicButton>
                                                <GhostButton
                                                    type='button'
                                                    $size='small'
                                                    disabled={!isLatest || hasLaterSubmission || isInteractionBusy}
                                                    onClick={() => void send(T._('Do not apply the proposed changes. I want to revise the plan.', 'Admin'))}
                                                >
                                                    {T._('Revise plan', 'Admin')}
                                                </GhostButton>
                                            </Stack>
                                        </ApprovalCard>
                                    )}
                                    {isLatest && (turn.gesture || syntheticGesture) && (
                                        <AgentGestureCard
                                            request={(turn.gesture || syntheticGesture)!}
                                            draft={draft}
                                            disabled={isInteractionBusy}
                                            onSubmit={submitGesture}
                                        />
                                    )}
                                </AgentBubble>
                            </MessageGroup>
                        );
                    })}

                    {(isBusy || isRestoring) && <AgentActivityIndicator request={currentRequest} restoring={isRestoring} refreshing={isRefreshingResult} />}
                </Transcript>

                {error && (
                    <ErrorNotice>
                        <span>{error}</span>
                        {canRetry && (
                            <BasicButton type='button' $size='small' disabled={isBusy || isDraftBusy} onClick={() => void retry()}>
                                {hasPendingRequest ? T._('Check progress', 'Admin') : T._('Try again', 'Admin')}
                            </BasicButton>
                        )}
                    </ErrorNotice>
                )}
                {!error && canRetry && (
                    <ErrorNotice>
                        <span>{T._('The request could not be completed. Would you like to try again?', 'Admin')}</span>
                        <BasicButton type='button' $size='small' disabled={isBusy || isDraftBusy} onClick={() => void retry()}>
                            {T._('Try again', 'Admin')}
                        </BasicButton>
                    </ErrorNotice>
                )}
                {composerError && <ErrorNotice>{composerError}</ErrorNotice>}
                {conversationId && wizardQuestionnaire && (
                    <AgentQuestionWizard
                        key={`${wizardQuestionnaire.turnId}-${answerCorrection?.afterTurnId ?? 'new'}`}
                        conversationId={conversationId}
                        questionnaire={wizardQuestionnaire}
                        disabled={isInteractionBusy || isRestoring}
                        initialAnswers={answerCorrection?.recap.answers}
                        correctionAfterTurnId={answerCorrection?.afterTurnId}
                        onCancel={answerCorrection ? () => setAnswerCorrection(null) : undefined}
                        onSubmit={async (submission) => {
                            const accepted = await sendAnswers(submission);
                            if (accepted) setAnswerCorrection(null);
                            return accepted;
                        }}
                    />
                )}
                <Composer>
                    {attachments.length > 0 && (
                        <Attachments>
                            {attachments.map((attachment) => (
                                <Attachment key={attachment.id}>
                                    <img src={attachment.previewUrl} alt={attachment.name} />
                                    <AttachmentInfo>
                                        <span>{attachment.name}</span>
                                        <small>
                                            {attachment.status === 'uploading'
                                                ? T._('Uploading…', 'Admin')
                                                : attachment.status === 'failed'
                                                  ? attachment.error
                                                  : T._('Ready', 'Admin')}
                                        </small>
                                        {variantNames.length > 1 && (
                                            <AttachmentSelect
                                                value={attachment.variant ?? ''}
                                                onChange={(event) => updateAttachment(attachment.id, { variant: event.target.value || null })}
                                            >
                                                <option value=''>{T._('Which variant?', 'Admin')}</option>
                                                {variantNames.map((variant) => (
                                                    <option key={variant}>{variant}</option>
                                                ))}
                                            </AttachmentSelect>
                                        )}
                                        {sideNames.length > 1 && (
                                            <AttachmentSelect
                                                value={attachment.side ?? ''}
                                                onChange={(event) => updateAttachment(attachment.id, { side: event.target.value || null })}
                                            >
                                                <option value=''>{T._('Which side?', 'Admin')}</option>
                                                {sideNames.map((side) => (
                                                    <option key={side}>{side}</option>
                                                ))}
                                            </AttachmentSelect>
                                        )}
                                    </AttachmentInfo>
                                    <RemoveAttachment
                                        type='button'
                                        aria-label={T._('Remove attachment', 'Admin')}
                                        onClick={() => removeAttachment(attachment.id)}
                                    >
                                        ×
                                    </RemoveAttachment>
                                </Attachment>
                            ))}
                        </Attachments>
                    )}
                    <ComposerDropZone
                        $dragging={isDraggingAttachments}
                        onDragEnter={handleComposerDragEnter}
                        onDragOver={handleComposerDragOver}
                        onDragLeave={handleComposerDragLeave}
                        onDrop={handleComposerDrop}
                        onPaste={handleComposerPaste}
                    >
                        {isDraggingAttachments && <ComposerDropHint>{T._('Drop images to attach', 'Admin')}</ComposerDropHint>}
                        <ChatInputArea>
                            <HiddenFileInput ref={fileInputRef} type='file' accept='image/*' multiple onChange={attachFiles} />
                            <AttachButton
                                type='button'
                                $type='icon'
                                $size='medium'
                                disabled={isInteractionBusy}
                                aria-label={T._('Attach images', 'Admin')}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <Icon>
                                    <AttachFileIcon />
                                </Icon>
                            </AttachButton>
                            <ChatInput
                                value={message}
                                disabled={isInteractionBusy}
                                placeholder={T._('Ask the assistant or describe what you need…', 'Admin')}
                                onChange={(event) => setMessage(event.target.value)}
                                onKeyDown={onComposerKeyDown}
                            />
                            <ChatSendButton
                                type='button'
                                $disabled={isInteractionBusy || (!message.trim() && attachments.every((attachment) => attachment.status !== 'ready'))}
                                disabled={isInteractionBusy || (!message.trim() && attachments.every((attachment) => attachment.status !== 'ready'))}
                                aria-label={T._('Send message', 'Admin')}
                                onClick={sendCurrentMessage}
                            />
                        </ChatInputArea>
                    </ComposerDropZone>
                </Composer>
            </ConversationPanel>

            {hasPreview && (
                <SplitHandle
                    type='button'
                    $dragging={isSplitDragging}
                    role='separator'
                    aria-orientation='vertical'
                    aria-valuemin={MIN_SPLIT_RATIO * 100}
                    aria-valuemax={MAX_SPLIT_RATIO * 100}
                    aria-valuenow={Math.round(splitRatio * 100)}
                    aria-label={T._('Resize conversation and preview panels', 'Admin')}
                    title={T._('Drag to resize conversation and preview panels', 'Admin')}
                    onPointerDown={handleSplitPointerDown}
                    onPointerMove={handleSplitPointerMove}
                    onPointerUp={handleSplitPointerEnd}
                    onPointerCancel={handleSplitPointerEnd}
                    onKeyDown={handleSplitKeyDown}
                    onDoubleClick={() => updateSplitRatio(DEFAULT_SPLIT_RATIO)}
                />
            )}

            {hasPreview && (
                <PreviewPanel $visible={activePanel === 'preview'}>
                    {isDraftRefreshRequired && (
                        <Notice>
                            {T._('The preview is out of date. Refresh it before making another edit.', 'Admin')}
                            <BasicButton type='button' $size='small' disabled={isInteractionBusy} onClick={() => void refreshPreview()}>
                                {T._('Refresh preview', 'Admin')}
                            </BasicButton>
                        </Notice>
                    )}
                    <AgentTaskPreview
                        draft={draft}
                        disabled={isInteractionBusy || isDraftRefreshRequired}
                        onUpsertPrintArea={upsertPrintArea}
                        onReplaceSideImage={replaceSideImage}
                        onDeletePrintArea={deletePrintArea}
                        onRestorePrintArea={restorePrintArea}
                        onSetSideScale={setSideScale}
                    />
                </PreviewPanel>
            )}
        </Root>
    );
};
